import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, Typography, Chip, Grid,
  TextField, Select, MenuItem, FormControl, CircularProgress,
  Alert, LinearProgress, IconButton, Tooltip, InputAdornment,
  Stack, Avatar, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import TuneIcon from '@mui/icons-material/Tune';
import { projectAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';

const STATUS = {
  planning:  { color: '#4472C4', label: 'Planning' },
  active:    { color: '#2E7D32', label: 'Active' },
  on_hold:   { color: '#E65100', label: 'On Hold' },
  completed: { color: '#0277BD', label: 'Completed' },
  cancelled: { color: '#C62828', label: 'Cancelled' },
};
const PRIORITY = {
  low:      { color: '#2E7D32', label: 'Low' },
  medium:   { color: '#F59E0B', label: 'Medium' },
  high:     { color: '#EF4444', label: 'High' },
  critical: { color: '#7C3AED', label: 'Critical' },
};

const fmtDate = (d) => {
  if (!d) return '—';
  const p = d.split('-');
  const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(p[2])} ${m[parseInt(p[1])-1]} ${p[0]}`;
};

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user, isSupportTeam, isAdmin } = useAuth();
  const canCreate = isSupportTeam() || isAdmin();
  const canUpdateProject = isSupportTeam() || isAdmin() || ['qa_team', 'team_member'].includes(user?.role);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ search: '', status: '', priority: '' });

  const load = useCallback(() => {
    setLoading(true);
    projectAPI.getAll(filters)
      .then(res => setProjects(res.data.projects || []))
      .catch(() => setError('Failed to load projects'))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const statusCounts = Object.keys(STATUS).reduce((acc, s) => {
    acc[s] = projects.filter(p => p.status === s).length;
    return acc;
  }, {});

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.5px' }}>Projects</Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 0.3 }}>
            {projects.length} project{projects.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => navigate('/projects/new')}
            sx={{ borderRadius: 2, fontWeight: 700, px: 2.5, boxShadow: '0 4px 14px rgba(30,58,95,0.25)' }}>
            New Project
          </Button>
        )}
      </Box>

      {/* Status summary chips */}
      {projects.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          {Object.entries(STATUS).map(([key, s]) => statusCounts[key] > 0 && (
            <Box key={key} onClick={() => setFilters(f => ({ ...f, status: f.status === key ? '' : key }))}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.5, py: 0.6, borderRadius: 5,
                cursor: 'pointer', border: '1px solid',
                borderColor: filters.status === key ? s.color : s.color + '30',
                bgcolor: filters.status === key ? s.color + '15' : 'transparent',
                transition: 'all 0.15s',
                '&:hover': { borderColor: s.color, bgcolor: s.color + '10' } }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: s.color }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: s.color, fontSize: '0.72rem' }}>
                {s.label}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 800, color: s.color, fontSize: '0.72rem' }}>
                {statusCounts[key]}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <CardContent sx={{ pb: '16px !important', pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <TuneIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
            <TextField size="small" placeholder="Search projects..."
              value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })}
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }} />
            <Select size="small" value={filters.status} displayEmpty sx={{ minWidth: { xs: '100%', sm: 130 }, borderRadius: 2 }}
              onChange={e => setFilters({ ...filters, status: e.target.value })}>
              <MenuItem value="">All Status</MenuItem>
              {Object.entries(STATUS).map(([k, s]) => <MenuItem key={k} value={k}>{s.label}</MenuItem>)}
            </Select>
            <Select size="small" value={filters.priority} displayEmpty sx={{ minWidth: { xs: '100%', sm: 130 }, borderRadius: 2 }}
              onChange={e => setFilters({ ...filters, priority: e.target.value })}>
              <MenuItem value="">All Priority</MenuItem>
              {Object.entries(PRIORITY).map(([k, p]) => <MenuItem key={k} value={k}>{p.label}</MenuItem>)}
            </Select>
            {(filters.search || filters.status || filters.priority) && (
              <Button size="small" onClick={() => setFilters({ search: '', status: '', priority: '' })}
                sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>Clear</Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={2.5}>
          {projects.map((p) => {
            const st = STATUS[p.status] || STATUS.planning;
            const pr = PRIORITY[p.priority] || PRIORITY.medium;
            const pct = p.completion_percentage || 0;
            return (
              <Grid item xs={12} sm={6} lg={4} xl={3} key={p.id}>
                <Card onClick={() => navigate(`/projects/${p.id}`)}
                  sx={{ height: '100%', cursor: 'pointer', borderRadius: 3, border: '1px solid', borderColor: 'divider',
                    boxShadow: 'none', transition: 'all 0.18s',
                    '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 12px 32px ${st.color}18`, borderColor: st.color + '50' } }}>

                  {/* Top color bar */}
                  <Box sx={{ height: 3, bgcolor: st.color, borderRadius: '12px 12px 0 0' }} />

                  <CardContent sx={{ p: 2.5 }}>
                    {/* Row 1: avatar + priority + edit */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Avatar sx={{ width: 44, height: 44, bgcolor: st.color + '18', color: st.color, fontWeight: 900, fontSize: '1.1rem', border: `2px solid ${st.color}25` }}>
                        {p.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Chip label={pr.label} size="small"
                          sx={{ bgcolor: pr.color + '15', color: pr.color, fontWeight: 700, fontSize: '0.62rem', height: 20, border: `1px solid ${pr.color}30` }} />
                        {canUpdateProject && (
                          <Tooltip title="Edit project">
                            <IconButton size="small" onClick={e => { e.stopPropagation(); navigate(`/projects/${p.id}/edit`); }}
                              sx={{ width: 28, height: 28, bgcolor: 'action.hover' }}>
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    {/* Name + desc */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5, letterSpacing: '-0.2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary"
                      sx={{ mb: 2.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: 36, fontSize: '0.8rem', lineHeight: 1.5 }}>
                      {p.description || 'No description provided.'}
                    </Typography>

                    {/* Progress */}
                    <Box sx={{ mb: 2.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Progress</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: st.color, fontSize: '0.8rem' }}>{pct}%</Typography>
                      </Box>
                      <Box sx={{ bgcolor: st.color + '15', borderRadius: 2, height: 7, overflow: 'hidden' }}>
                        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: st.color, borderRadius: 2, transition: 'width 0.5s ease', minWidth: pct > 0 ? 4 : 0 }} />
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 2, borderStyle: 'dashed' }} />

                    {/* Footer */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Chip label={st.label} size="small"
                        sx={{ bgcolor: st.color + '15', color: st.color, fontWeight: 700, fontSize: '0.68rem', height: 22, border: `1px solid ${st.color}25` }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarTodayIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                          {fmtDate(p.end_date)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}

          {projects.length === 0 && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 10, bgcolor: 'background.paper', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
                <FolderOpenIcon sx={{ fontSize: 56, opacity: 0.12, display: 'block', mx: 'auto', mb: 1.5 }} />
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700 }}>No projects found</Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                  {filters.search || filters.status || filters.priority ? 'Try adjusting your filters' : 'Create your first project to get started'}
                </Typography>
                {canCreate && !filters.search && !filters.status && !filters.priority && (
                  <Button variant="contained" startIcon={<AddIcon />} sx={{ mt: 2.5, borderRadius: 2, fontWeight: 700 }}
                    onClick={() => navigate('/projects/new')}>
                    Create First Project
                  </Button>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
}